// What this report actually covers, said out loud (clinic row scoping).

import { Info } from 'lucide-react';
import type { DatasetManifestEntry } from '../../types/reporting';

export interface ReportScopeNoticeProps {
  /** The report's primary dataset, or null before one is chosen. */
  dataset: DatasetManifestEntry | null;
  /** Related datasets explicitly joined into this report, in join order. */
  joined?: DatasetManifestEntry[];
}

// Keyed on clinic ids, not names: two clinics can share a display name, and a
// name-keyed comparison would read their scopes as equal and stay silent about
// a join that in fact straddles them. Names remain the fallback only for a
// manifest minted before clinic_ids existed.
const scopeKey = (entry: DatasetManifestEntry): string => {
  const scope = entry.clinic_scope;
  if (scope?.kind !== 'clinics') return 'agency';
  const identity =
    scope.clinic_ids && scope.clinic_ids.length > 0
      ? scope.clinic_ids
      : scope.clinic_names;
  return `clinics:${[...identity].sort().join('|')}`;
};

/** "Northside", "Northside and Eastside", "Northside, Eastside and Southside". */
const listClinics = (names: string[]): string => {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const listNames = (entries: DatasetManifestEntry[]): string =>
  listClinics(entries.map(entry => entry.label));

/**
 * A one-line statement of which clinics the numbers above cover, plus a warning
 * when a join spans datasets the caller sees differently.
 *
 * **Why this exists at all.** A filtered total looks exactly like an unfiltered
 * one. Two colleagues can open the same saved report, read different numbers,
 * and have no way to discover why except by arguing about it. Every other part
 * of clinic scoping fails closed; this is the part that can mislead, so it is
 * the part that has to speak.
 *
 * Nothing here is a permission check — the server filtered the rows before they
 * reached the browser. This is prose about a decision already made.
 *
 * **Copy standard (ASD-STE100-like).** Every sentence here states one idea, in
 * the active voice, under about twenty words. No phrasal verbs ("come through
 * blank"), no subordinate clauses hung off dashes, and one term per concept —
 * a cell is always *empty*, never blank/missing/null. The reader is a clinical
 * manager who has just been told their totals are not what they look like;
 * that is the worst moment to make someone parse a long sentence. Keep new
 * copy here to the same shape, and never put a ticket id in it.
 */
export const ReportScopeNotice = ({
  dataset,
  joined = [],
}: ReportScopeNoticeProps) => {
  if (!dataset) return null;

  const scope = dataset.clinic_scope;
  // An older cached bundle, or a manifest built before this field existed,
  // reads as unbounded — matching the behaviour that shipped before row
  // filtering, and staying silent rather than claiming a scope we don't know.
  if (!scope || scope.kind === 'agency') {
    // A join can still straddle scopes even when the PRIMARY is agency-wide —
    // in fact that is the worst case: full visits joined to one clinic's claims
    // makes the claim columns read as missing data rather than as work the
    // reader may not see.
    const narrowed = joined.filter(
      entry => entry.clinic_scope?.kind === 'clinics'
    );
    if (narrowed.length === 0) return null;
    return (
      <Notice
        tone='warn'
        text={`You can see ${listNames(narrowed)} for some clinics only. Values from the other clinics are empty. Totals are lower than the agency total.`}
      />
    );
  }

  const covers =
    scope.clinic_names.length === 0
      ? 'This report shows no data. Your access to this report does not include a clinic.'
      : `This report shows data from ${listClinics(scope.clinic_names)} only. It does not include the other clinics in your agency.`;

  // Compared by resolved scope rather than by kind: two datasets can both be
  // clinic-scoped and still disagree, which produces the same empty-column
  // confusion as scoped-vs-agency.
  const mismatched = joined.filter(
    entry => scopeKey(entry) !== scopeKey(dataset)
  );

  return (
    <Notice
      tone={mismatched.length > 0 ? 'warn' : 'info'}
      text={
        mismatched.length > 0
          ? `${covers} You can see ${listNames(mismatched)} for different clinics. Values from clinics you cannot see are empty. Totals can be too low.`
          : covers
      }
    />
  );
};

const Notice = ({ tone, text }: { tone: 'info' | 'warn'; text: string }) => (
  <p
    className={`mb-2 inline-flex w-fit items-start gap-1.5 rounded-md border px-2 py-1 text-xs ${
      tone === 'warn'
        ? 'border-amber-300 bg-amber-50 text-amber-900'
        : 'border-border bg-muted/40 text-muted-foreground'
    }`}
  >
    <Info className='mt-0.5 h-3.5 w-3.5 shrink-0' />
    <span>{text}</span>
  </p>
);

export default ReportScopeNotice;
